'use client'

import React from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { Label } from '@/ui/atoms/Label'
import { Header } from '@/ui/components/Header'
import { Title } from '@/ui/components/Title'
import RichText from '@/ui/components/RichText'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'

export type MilestoneData = {
  title: string
  date: string
  category: string
  description: string | SerializedEditorState
  isTitleLink?: boolean | null
  titleLink?: string | null
  titleLinkNewTab?: boolean | null
  categoryVariant?:
    | 'neutral'
    | 'blue'
    | 'light-blue'
    | 'subtle-blue'
    | 'error'
    | 'success'
    | 'warning'
    | 'transparent'
    | 'transparentInverted'
    | 'orange'
    | 'plain'
}

interface CompanyMilestonesProps {
  label?: string
  title?: string
  subtitle?: string | SerializedEditorState
  milestones: MilestoneData[]
  className?: string
}

export const CompanyMilestones: React.FC<CompanyMilestonesProps> = ({
  label,
  title = 'Company Milestones & News',
  subtitle = 'We are a young startup launched in 2025 and here are some of our key achievements and updates as we grow',
  milestones,
  className,
}) => {
  return (
    <div className={classNames('w-full', className)}>
      {/* Header Section */}
      <Header label={label} title={title} description={subtitle} />

      {/* Timeline */}
      <div className="relative">
        {milestones.map((milestone, index) => (
          <MilestoneCard key={index} {...milestone} />
        ))}
      </div>
    </div>
  )
}

const MilestoneCard: React.FC<MilestoneData> = ({
  title,
  date,
  category,
  description,
  isTitleLink = false,
  titleLink,
  titleLinkNewTab = false,
  categoryVariant = 'blue',
}) => {
  const isExternalLink = titleLink ? (
    titleLink.startsWith('http://') ||
    titleLink.startsWith('https://') ||
    titleLink.startsWith('mailto:') ||
    titleLink.startsWith('tel:')
  ) : false
  const linkProps = titleLinkNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}
  const hasLink = Boolean(isTitleLink && titleLink)

  const titleLinkClasses =
    'after:absolute after:inset-0 after:rounded-lg after:content-[""] group-hover:text-text-link transition-colors'

  const titleNode =
    hasLink && titleLink ? (
      isExternalLink ? (
        <a href={titleLink} className={titleLinkClasses} {...linkProps}>
          {title}
        </a>
      ) : (
        <Link href={titleLink} className={titleLinkClasses} {...linkProps}>
          {title}
        </Link>
      )
    ) : (
      title
    )

  const cardBodyClasses = classNames(
    'relative bg-white border border-border-subtle rounded-lg p-5 lg:p-6',
    hasLink && 'group transition-colors duration-300 ease-in-out hover:bg-blue-95 cursor-pointer',
  )

  return (
    <div className="relative pb-10 last:pb-0">
      <div className="w-full">
        <div className={cardBodyClasses}>
          {/* Date and Category */}
          <div className="flex items-center gap-2.5 flex-wrap mb-3">
            <span className="text-label-XS-regular text-text-faded">{date}</span>
            <span className="w-1 h-1 rounded-full bg-border-base" />
            <Label title={category} variant={categoryVariant} size="small" />
          </div>

          {/* Title */}
          <h3 className="text-title-M-medium text-text-base mb-3 break-words">{titleNode}</h3>

          {/* Description — inline <a>s need relative/z-10 to stack above the title's stretched ::after overlay */}
          {typeof description === 'string' ? (
            <p className="text-body-S-regular text-text-subtle leading-relaxed">{description}</p>
          ) : (
            <RichText
              data={description}
              className="text-body-S-regular text-text-subtle prose-p:!text-body-S-regular prose-p:!text-text-subtle prose-p:!mb-0 prose-p:!leading-relaxed [&_a]:relative [&_a]:z-10"
              enableGutter={false}
              enableProse={false}
            />
          )}
        </div>
      </div>
    </div>
  )
}
